import { useMemo } from 'react';
import { Users, User, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface Participant {
  id: string;
  user_id: string;
  player1_name: string;
  player2_name: string | null;
  registered_at: string;
  group: string | null;
}

interface ParticipantsListProps {
  participants: Participant[];
  currentUserId: string | null;
  loading: boolean;
  subcategory: string;
  category: string;
}

export default function ParticipantsList({
  participants,
  currentUserId,
  loading,
  subcategory,
  category
}: ParticipantsListProps) {
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();

  const translateCategory = (cat: string) => {
    if (language === 'es') return cat;
    const normalized = cat.toLowerCase();
    const exact = t(`categories.${normalized}`);
    if (exact !== `categories.${normalized}`) return exact;
    const parts = cat.split(' ');
    if (parts.length >= 2) {
      const gender = t(`categories.${parts[0].toLowerCase()}`);
      const sub = t(`subcategories.${parts.slice(1).join(' ')}`);
      const g = gender !== `categories.${parts[0].toLowerCase()}` ? gender : parts[0];
      const s = sub !== `subcategories.${parts.slice(1).join(' ')}` ? sub : parts.slice(1).join(' ');
      return `${g} ${s}`;
    }
    return cat;
  };

  const translateSubcategory = (sub: string) => {
    if (language === 'es') return sub;
    const translated = t(`subcategories.${sub}`);
    return translated !== `subcategories.${sub}` ? translated : sub;
  };

  const groupedParticipants = useMemo(() => {
    const groups: Record<string, Participant[]> = {};
    participants.forEach(p => {
      const key = p.group || (language === 'es' ? 'Sin grupo' : 'No group');
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      const noGroup = language === 'es' ? 'Sin grupo' : 'No group';
      if (a === noGroup) return 1;
      if (b === noGroup) return -1;
      return a.localeCompare(b);
    });
  }, [participants, language]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            {t('tournament.loadingParticipants')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold`}>
              {t('tournament.registeredParticipants')}
            </span>
          </div>
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
            {participants.length} {participants.length === 1 ? t('tournament.singlePair') : t('tournament.pairs')}
          </Badge>
        </CardTitle>
        <div className="text-sm text-muted-foreground mt-2">
          {t('tournament.category')}: <span className="font-semibold">{translateCategory(category)}</span> • 
          {t('tournament.subcategory')}: <span className="font-semibold">{translateSubcategory(subcategory)}</span>
        </div>
      </CardHeader>
      
      <CardContent className="px-0">
        <ScrollArea className={isMobile ? "h-[400px]" : "h-[500px]"}>
          <div className="space-y-4 px-4">
            {groupedParticipants.map(([groupName, groupParticipants]) => (
              <div key={groupName}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-foreground`}>
                    {language === 'es' ? groupName : groupName.replace(/^Grupo\b/i, t('tournament.groupPrefix'))}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {groupParticipants.length} {groupParticipants.length === 1 ? t('tournament.singlePair') : t('tournament.pairs')}
                  </Badge>
                </div>

                <div className="space-y-2">
                  {groupParticipants.map((participant, index) => {
                    const isCurrentUser = participant.user_id === currentUserId;
                    const displayName = participant.player2_name
                      ? `${participant.player1_name} / ${participant.player2_name}`
                      : participant.player1_name;

                    return (
                      <div
                        key={participant.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          isCurrentUser
                            ? 'bg-primary/10 border-primary/30 shadow-sm'
                            : 'bg-card border-border hover:border-muted-foreground/30'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            {index === 0 ? (
                              <Crown className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-muted-foreground min-w-[24px] text-center`}>
                                #{index + 1}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className={`${isMobile ? 'text-xs' : 'text-base'} font-medium ${
                              isCurrentUser ? 'text-primary' : 'text-foreground'
                            }`}>
                              {displayName}
                            </span>
                            {isCurrentUser && (
                              <Badge className="bg-primary text-primary-foreground text-xs flex-shrink-0">
                                {t('tournament.you').toUpperCase()}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {participants.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t('tournament.noParticipants')}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
